// Inline file viewer — rendered below an open R2 row inside a drawer.
// Text files can be edited inline; binary files are read-only with
// download links. Save sends `If-Match: <etag>` so a concurrent agent
// write surfaces as a 412 instead of overwriting.
//
// Shared by InspectorDrawer (admin) and WorkspaceDrawer (deck owner)
// so both surfaces give the same view + edit affordance for the same
// R2 path.

import React, { useEffect, useState } from 'react';

export type FileContent =
  | { kind: 'text'; text: string; size: number; mime: string; etag?: string }
  | { kind: 'binary'; size: number; mime: string; download_url: string; etag?: string };

export interface InlineFileViewerProps {
  deckId: string;
  r2Key: string;
  onClose: () => void;
  content: FileContent | null;
  loading: boolean;
  error: string;
  /** Called after a successful save so the parent drawer can refresh
   *  its listing (size / uploaded_at may change). */
  onSaved?: (newEtag: string, newSize: number) => void;
}

export default function InlineFileViewer({
  deckId, r2Key, onClose, content, loading, error, onSaved,
}: InlineFileViewerProps) {
  const shortKey = r2Key.replace(`decks/${deckId}/`, '');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  useEffect(() => {
    setEditing(false);
    setDraft('');
    setSaveErr('');
  }, [r2Key]);

  const startEdit = () => {
    if (content?.kind === 'text') {
      setDraft(content.text);
      setEditing(true);
      setSaveErr('');
    }
  };
  const cancelEdit = () => {
    setEditing(false);
    setDraft('');
    setSaveErr('');
  };
  const save = async () => {
    if (content?.kind !== 'text') return;
    setSaving(true);
    setSaveErr('');
    try {
      const res = await fetch(`/api/decks/${deckId}/files?key=${encodeURIComponent(r2Key)}`, {
        method: 'PUT',
        headers: {
          'content-type': content.mime,
          ...(content.etag ? { 'If-Match': content.etag } : {}),
        },
        body: draft,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({})) as { error?: { message?: string } };
        if (res.status === 412) {
          throw new Error('File changed since you opened it. Close and reopen to see the latest, then re-apply your changes.');
        }
        throw new Error(j?.error?.message || `HTTP ${res.status}`);
      }
      const r = (await res.json()) as { ok: boolean; size: number; etag: string };
      onSaved?.(r.etag, r.size);
      setEditing(false);
    } catch (e: any) {
      setSaveErr(e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const isEditable = content?.kind === 'text';
  const dirty = editing && content?.kind === 'text' && draft !== content.text;
  const isMemoryPath = /^(memory|context)\//.test(shortKey);

  const card: React.CSSProperties = {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 6,
    padding: 10,
    fontSize: 'var(--fs-2xs)',
  };
  const headerRow: React.CSSProperties = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 6, gap: 6,
  };
  const btn: React.CSSProperties = {
    background: 'none', border: '1px solid var(--border)', cursor: 'pointer',
    color: 'var(--text-secondary)', fontSize: 'var(--fs-2xs)',
    padding: '3px 8px', borderRadius: 4, fontFamily: 'inherit',
  };
  const btnPrimary: React.CSSProperties = {
    ...btn, background: 'var(--brand, #0033FF)', borderColor: 'var(--brand, #0033FF)',
    color: 'var(--ds-on-accent)', fontWeight: 600,
  };
  const pre: React.CSSProperties = {
    background: 'var(--bg-primary)', border: '1px solid var(--border)',
    borderRadius: 4, padding: 8, fontSize: 'var(--fs-2xs)',
    fontFamily: 'monospace', overflow: 'auto', whiteSpace: 'pre',
    maxHeight: 360, margin: 0, color: 'var(--text-primary)',
  };
  const textarea: React.CSSProperties = {
    ...pre, width: '100%', minHeight: 240, maxHeight: 480, resize: 'vertical',
    whiteSpace: 'pre', boxSizing: 'border-box',
    outline: 'none',
  };

  return (
    <div style={card}>
      <div style={headerRow}>
        <span style={{ fontFamily: 'monospace', color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {shortKey}
        </span>
        <div style={{ display: 'flex', gap: 4 }}>
          {isEditable && !editing && (
            <button onClick={startEdit} style={btn} title="Edit text content">edit</button>
          )}
          {editing && (
            <>
              <button onClick={save} disabled={saving || !dirty} style={{ ...btnPrimary, opacity: saving || !dirty ? 0.55 : 1, cursor: saving ? 'wait' : 'pointer' }}>
                {saving ? 'saving…' : 'save'}
              </button>
              <button onClick={cancelEdit} disabled={saving} style={btn}>cancel</button>
            </>
          )}
          <button onClick={onClose} style={btn} title="Close">close</button>
        </div>
      </div>
      {loading && <div style={{ color: 'var(--text-muted)' }}>Loading…</div>}
      {error && (
        <div style={{ color: 'var(--ds-danger)', padding: 6, background: 'var(--ds-danger-soft)', borderRadius: 4 }}>
          {error}
        </div>
      )}
      {saveErr && (
        <div style={{ color: 'var(--ds-danger)', padding: 6, background: 'var(--ds-danger-soft)', borderRadius: 4, marginBottom: 6 }}>
          {saveErr}
        </div>
      )}
      {editing && isMemoryPath && (
        <div style={{ color: 'var(--ds-warning)', padding: 6, background: 'var(--ds-accent-soft)', borderRadius: 4, marginBottom: 6, fontSize: 'var(--fs-2xs)' }}>
          ⚠ Editing {shortKey.split('/')[0]}/ files — the chat panel will refresh and the agent will see your edit on the next turn.
        </div>
      )}
      {content?.kind === 'text' && !editing && (
        <>
          <pre style={pre}>{content.text}</pre>
          <div style={{ marginTop: 4, color: 'var(--text-muted)' }}>
            {content.mime} · {content.size.toLocaleString()} bytes
            {' · '}
            <a href={`/api/decks/${deckId}/files?key=${encodeURIComponent(r2Key)}&raw=1`} target="_blank" rel="noreferrer noopener" style={{ color: 'var(--text-muted)' }}>
              download
            </a>
          </div>
        </>
      )}
      {content?.kind === 'text' && editing && (
        <>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            style={textarea}
            spellCheck={false}
          />
          <div style={{ marginTop: 4, color: 'var(--text-muted)' }}>
            {content.mime} · {draft.length.toLocaleString()} chars · {dirty ? 'modified' : 'unchanged'}
          </div>
        </>
      )}
      {content?.kind === 'binary' && content.mime.startsWith('image/') && (
        <>
          <div style={{ background: 'var(--ds-surface)', border: '1px solid var(--border)', borderRadius: 4, padding: 6, display: 'flex', justifyContent: 'center' }}>
            <img
              src={content.download_url}
              alt={shortKey}
              style={{ maxWidth: '100%', maxHeight: 360, objectFit: 'contain' }}
            />
          </div>
          <div style={{ marginTop: 4, color: 'var(--text-muted)' }}>
            {content.mime} · {content.size.toLocaleString()} bytes ·{' '}
            <a href={content.download_url} target="_blank" rel="noreferrer noopener" style={{ color: 'var(--text-muted)' }}>
              download
            </a>
          </div>
        </>
      )}
      {content?.kind === 'binary' && !content.mime.startsWith('image/') && (
        <div>
          <div style={{ color: 'var(--text-secondary)' }}>
            Binary file ({content.mime}) — {content.size.toLocaleString()} bytes.
          </div>
          <a
            href={content.download_url}
            target="_blank"
            rel="noreferrer noopener"
            style={{ display: 'inline-block', marginTop: 6, padding: '4px 10px', background: 'var(--brand, #0033FF)', color: 'var(--ds-on-accent)', borderRadius: 4, textDecoration: 'none', fontSize: 'var(--fs-xs)' }}
          >
            Download
          </a>
        </div>
      )}
    </div>
  );
}
